import React, { useCallback, useMemo } from 'react';
import type { MerchandiseCard as MerchandiseCardType } from '@honeycomb/data';
import {
    CarouselDisplayStyle,
    ImageOrigin,
    MerchandiseCardBorderPosition,
    useGlobalContext,
    useImageGroupCard,
} from '@honeycomb/data';
import {
    Anchor,
    Button,
    Card,
    ColorOption,
    Image,
    ImageContent,
    ImageSet,
    Observer,
    Stacker,
    Styler,
    StylerProps,
    TypographyBody,
    TypographyHero,
} from '@honeycomb/ui-core';
import {
    augmentImageUrl,
    generatePixel,
    ImageFormat,
    mapCMSBackgroundColorToThemeColor,
    mapCMSTextColorToThemeColor,
    mapMerchandiseCardsWithAds,
    MerchandiseCardProps,
} from '@honeycomb/ui';
import { TrRoundelBadge } from './TrRoundelBadge';
import { TrSquareBadge } from './TrSquareBadge';

const BORDER_WIDTH_Y = 10;
const BORDER_WIDTH_X = 3;
const IMAGE_BORDER_RADIUS = 20;

const TEXT_SHADOW = '0 5px 10px rgba(0, 0, 0, 0.5)';

const CARD_HEIGHT = {
    default: 440,
    fullWidth: 480,
};

const getBorderPadding = (
    borderPosition: MerchandiseCardBorderPosition
): { pt: number; pr: number; pb: number; pl: number } => {
    switch (borderPosition) {
        case MerchandiseCardBorderPosition.TopLeft:
            return { pt: BORDER_WIDTH_Y, pr: 0, pb: 0, pl: BORDER_WIDTH_X };
        case MerchandiseCardBorderPosition.TopRight:
            return { pt: BORDER_WIDTH_Y, pr: BORDER_WIDTH_X, pb: 0, pl: 0 };
        case MerchandiseCardBorderPosition.BottomLeft:
            return { pt: 0, pr: 0, pb: BORDER_WIDTH_Y, pl: BORDER_WIDTH_X };
        case MerchandiseCardBorderPosition.BottomRight:
            return { pt: 0, pr: BORDER_WIDTH_X, pb: BORDER_WIDTH_Y, pl: 0 };
        default:
            return { pt: 0, pr: 0, pb: 0, pl: 0 };
    }
};

const getImageBorderRadius = (borderPosition: MerchandiseCardBorderPosition): Partial<StylerProps> => {
    switch (borderPosition) {
        case MerchandiseCardBorderPosition.TopLeft:
            return { br: 0, btlr: IMAGE_BORDER_RADIUS };
        case MerchandiseCardBorderPosition.TopRight:
            return { br: 0, btrr: IMAGE_BORDER_RADIUS };
        case MerchandiseCardBorderPosition.BottomLeft:
            return { br: 0, bblr: IMAGE_BORDER_RADIUS };
        case MerchandiseCardBorderPosition.BottomRight:
            return { br: 0, bbrr: IMAGE_BORDER_RADIUS };
        default:
            return { br: 0, btlr: IMAGE_BORDER_RADIUS };
    }
};

const getPromoTextPosition = (
    borderPosition: MerchandiseCardBorderPosition
): Partial<{ top: number; bottom: number }> => {
    switch (borderPosition) {
        case MerchandiseCardBorderPosition.TopLeft:
        case MerchandiseCardBorderPosition.TopRight:
            return { top: 3 };
        case MerchandiseCardBorderPosition.BottomLeft:
        case MerchandiseCardBorderPosition.BottomRight:
            return { bottom: 3 };
        default:
            return {};
    }
};

function getOverlayLogo(overlayLogoUrl: string, carouselDisplayStyle: CarouselDisplayStyle) {
    if (carouselDisplayStyle === CarouselDisplayStyle.FullWidth) {
        return (
            <Styler maw="100%" mah={{ xs: '100px', s: '150px', m: '200px' }} mb={4}>
                <Image src={overlayLogoUrl} alt="overlay logo" w="auto" fit="contain" />
            </Styler>
        );
    }
    return <Image src={overlayLogoUrl} alt="overlay logo" maw={120} pos="absolute" top={4} right={4} />;
}

export function TrBrandMerchandiseCard(props: MerchandiseCardProps): React.JSX.Element {
    const {
        resourceStrings: { HC_MERCH_CARD_DEFAULT_BUTTON },
    } = useGlobalContext();
    const { card, moreLinkText, carouselDisplayStyle, zoneIds } = props;

    const [cardData, setCardData] = React.useState<MerchandiseCardType>({});
    const [getPlatform195ImageCardsQuery] = useImageGroupCard();

    const {
        promotionText1: borderText,
        imageUrl,
        mobileImageUrl,
        overlayLogo,
        overlayText1,
        overlayText2,
        overlayText3,
        imageLink,
        impressionUrl,
        clickUrl,
        viewEventUrl,
        useExternalImageSource,
        borderBackgroundColor: borderBackgroundColorProp,
        borderTextColor: borderTextColorProp,
        borderPosition,
        promotionalBadge,
    } = card.zoneId ? cardData : card;

    React.useEffect(() => {
        if (!zoneIds || !card.zoneId) {
            return;
        }

        const fetchData = async () => {
            try {
                const { data } = await getPlatform195ImageCardsQuery({ variables: { zoneIds } });
                const values = mapMerchandiseCardsWithAds(card, data?.getPlatform195ImageCards);
                setCardData(values);
            } catch (error) {
                console.error('Error while processing merchandise data', error);
            }
        };

        fetchData();
    }, [getPlatform195ImageCardsQuery, zoneIds, card]);

    const needsObserving = !!impressionUrl && !!viewEventUrl;
    const isFullWidth = carouselDisplayStyle === CarouselDisplayStyle.FullWidth;

    const withBorder = Boolean(borderPosition);

    // Colour mappings
    const borderBackgroundColor: ColorOption = borderBackgroundColorProp
        ? mapCMSBackgroundColorToThemeColor(borderBackgroundColorProp)
        : 'background.content';

    const borderTextColor: ColorOption = borderTextColorProp
        ? mapCMSTextColorToThemeColor(borderTextColorProp)
        : 'text.body';

    // Border properties
    const borderPadding = borderPosition ? getBorderPadding(borderPosition) : null;
    const imageBorderRadius = borderPosition ? getImageBorderRadius(borderPosition) : null;
    const promoTextPosition = borderPosition ? getPromoTextPosition(borderPosition) : null;

    const imageSetData: ImageSet = useMemo(() => {
        const imageOrigin = ImageOrigin.Facade;

        const format = ImageFormat.Webp;
        if (!useExternalImageSource && imageUrl) {
            if (isFullWidth) {
                const type = 'fullWidthMerchCard';
                return {
                    l: augmentImageUrl(imageOrigin, imageUrl, type, { breakpoint: 'l', format }),
                    m: augmentImageUrl(imageOrigin, imageUrl, type, { breakpoint: 'm', format }),
                    s: augmentImageUrl(imageOrigin, imageUrl, type, { breakpoint: 's', format }),
                    xs: augmentImageUrl(imageOrigin, imageUrl, type, { breakpoint: 'xs', format }),
                };
            }
            return augmentImageUrl(imageOrigin, imageUrl, 'merchandiseCard');
        }
        return { xs: mobileImageUrl ?? imageUrl ?? '', m: imageUrl ?? '' };
    }, [imageUrl, isFullWidth, mobileImageUrl, useExternalImageSource]);

    const navigatesTo = clickUrl || imageLink || null;

    const heroHeading = (
        <TypographyHero size={isFullWidth ? '2xl' : 'l'} textShadow={TEXT_SHADOW}>
            {overlayText1}
        </TypographyHero>
    );

    const cardComponent = (
        <Card
            h={isFullWidth ? CARD_HEIGHT.fullWidth : CARD_HEIGHT.default}
            // The border is formed by applying background colour and padding to the surface `Paper` component.
            SurfaceProps={{
                cornerStyle: 'rounded',
                radius: withBorder ? 'xs' : 'm',
                bg: borderBackgroundColor,
                c: borderTextColor,
                ...borderPadding,
            }}
        >
            <Image
                src={imageSetData}
                withPlaceholder={true}
                height="100%"
                backgroundMask="linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 80%)"
                {...imageBorderRadius}
            >
                {/* overlay logo for displayStyle Default */}
                {overlayLogo && carouselDisplayStyle && getOverlayLogo(overlayLogo, carouselDisplayStyle)}

                <ImageContent
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    alignItems="center"
                    p={{ xs: 4 }}
                    pb={{ xs: 8, l: isFullWidth ? 12 : 8 }}
                >
                    {promotionalBadge && promotionalBadge.text && (
                        <TrRoundelBadge badge={promotionalBadge} pos="absolute" top={4} left={4} />
                    )}

                    {/* overlay logo for displayStyle FullWidth */}
                    {overlayLogo && isFullWidth && getOverlayLogo(overlayLogo, carouselDisplayStyle)}

                    {isFullWidth && borderText && !borderPosition && (
                        <TrSquareBadge
                            backgroundColor={borderBackgroundColor}
                            textColor={borderTextColor}
                            text={borderText}
                        />
                    )}

                    {/* Text content */}
                    <Stacker
                        spacing={2}
                        alignItems="center"
                        ta="center"
                        c="common.white"
                        textShadow={TEXT_SHADOW}
                        maw={isFullWidth ? 900 : undefined}
                        my={4}
                    >
                        {navigatesTo ? (
                            <Anchor href={navigatesTo} blockLink={true} underline="none">
                                {heroHeading}
                            </Anchor>
                        ) : (
                            heroHeading
                        )}

                        {overlayText2 && <TypographyBody weight="semiBold">{overlayText2}</TypographyBody>}
                        {overlayText3 && <TypographyBody weight="semiBold">{overlayText3}</TypographyBody>}
                    </Stacker>

                    {navigatesTo && (
                        <Button
                            href={navigatesTo}
                            size={isFullWidth ? 'l' : 'm'}
                            color="white"
                            variant="filled"
                            c="primary"
                        >
                            {moreLinkText || HC_MERCH_CARD_DEFAULT_BUTTON}
                        </Button>
                    )}
                </ImageContent>
            </Image>

            {/* Promo text */}
            {withBorder && borderText && (
                <TypographyHero weight="bold" size="xs" pos="absolute" left={BORDER_WIDTH_X} {...promoTextPosition}>
                    {borderText}
                </TypographyHero>
            )}
        </Card>
    );

    const onImpression = useCallback(() => impressionUrl && generatePixel(impressionUrl), [impressionUrl]);
    const onViewComplete = useCallback(() => viewEventUrl && generatePixel(viewEventUrl), [viewEventUrl]);

    if (needsObserving) {
        return (
            <Observer onImpression={onImpression} onViewComplete={onViewComplete}>
                {cardComponent}
            </Observer>
        );
    }
    return cardComponent;
}
